import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Row } from '../core';
import {
  useChain,
  formatAmountBN,
  useAdmin,
  useTimelockByChain,
  useScionVault,
  useCall2,
  useScionStrategies,
} from '@sc1/common';
import { HarvestVault } from './HarvestVault';
import { ClaimFees } from './ClaimFees';
import { SetAllowed } from './SetAllowed';
import { SeizeStrategy } from './SeizeStrategy';
import { ExecutorTable } from './ExecutorTable';
import { SetTimelockRole } from './SetTimelockRole';
import {
  AddressMethod,
  AmountMethod,
  BooleanAddressMethod,
  BooleanMethod,
  SimpleMethod,
} from '../methods';
import { AdminInfo } from './AdminInfo';

export const ScionAdmin = (): JSX.Element | null => {
  // url params
  const { vaultAddress } = useParams();
  const chain = useChain();

  // get abis
  const vault = useScionVault(vaultAddress);
  const { strategies } = useScionStrategies(chain, vault);
  const timelock = useTimelockByChain(chain);

  const { res: minDelayBN } = useCall2(timelock?.getMinDelay);
  const minDelayFmt = formatAmountBN(minDelayBN, 0, false, 0);

  const { res: isPublic } = useCall2(vault?.isPublic);

  const { isVaultManager, isProposer, isExecutor, isTimelockAdmin } =
    useAdmin();

  return (
    <Box>
      <AdminInfo
        isVaultManager={isVaultManager}
        isProposer={isProposer}
        isExecutor={isExecutor}
        isTimelockAdmin={isTimelockAdmin}
      />
      {/* Executor Methods */}
      <ExecutorTable timelock={timelock} disabled={!isExecutor} />

      {/* Manager Methods */}
      <Row variant="collapse">
        <HarvestVault
          sx={{ flex: 1, my: 0 }}
          vault={vault}
          disabled={!isVaultManager}
          strategies={strategies}
        />
        <Box m={1} />
        <ClaimFees
          sx={{ flex: 1, my: 0 }}
          vault={vault}
          disabled={!isVaultManager}
        />
      </Row>

      <AmountMethod
        method="setMaxTvl"
        label="Set Max TVL"
        contract={vault}
        disabled={!isVaultManager}
      />
      <SimpleMethod
        contract={vault}
        method="updateStratTvl"
        label="Update Strat TVL"
        disabled={!isVaultManager}
      />
      <SetAllowed vault={vault} disabled={!isVaultManager} />
      <SeizeStrategy vault={vault} disabled={!isVaultManager} />

      {/* Proposer Methods */}
      <AmountMethod
        method="setFeePercent"
        label="Set Fee Percent (as decimal)"
        contract={vault}
        timelock={timelock}
        disabled={!isProposer}
        decimals={18}
      />
      <BooleanMethod
        method="setPublic"
        label="Set Public"
        labelTrue="Set Public"
        labelFalse="Set Private"
        disableTrue={isPublic}
        disableFalse={isPublic === false}
        contract={vault}
        disabled={!isProposer}
      />
      <BooleanAddressMethod
        method="setManager"
        label="Set Vault Manager"
        labelTrue="Assign"
        labelFalse="Revoke"
        contract={vault}
        timelock={timelock}
        disabled={!isProposer}
      />
      <AddressMethod
        method="addStrategy"
        label="Add Strategy"
        contract={vault}
        timelock={timelock}
        disabled={!isProposer}
      />
      <AddressMethod
        method="trustStrategy"
        label="Trust Strategy"
        contract={vault}
        timelock={timelock}
        disabled={!isProposer}
      />
      <AddressMethod
        method="distrustStrategy"
        label="Distrust Strategy"
        contract={vault}
        timelock={timelock}
        disabled={!isProposer}
      />

      {/* Timelock-related Methods */}
      <SetTimelockRole timelock={timelock} disabled={!isTimelockAdmin} />
      <AmountMethod
        method="updateDelay"
        label="Set Timelock Delay (in seconds)"
        hint={`Current Delay: ${minDelayFmt || 'N/A'} seconds`}
        timelock={timelock}
        contract={timelock}
        disabled={!isProposer}
        decimals={0}
      />
    </Box>
  );
};
